<template>
  <div class="map-wrapper">
    <div ref="mapEl" class="map-canvas"></div>

    <div class="pin-container" :class="{ dragging: isDragging }" aria-hidden="true">
      <div class="pin-body">
        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="48" viewBox="0 0 36 48">
          <path
            d="M18 1C8.611 1 1 8.611 1 18c0 13.8 17 29 17 29S35 31.8 35 18C35 8.611 27.389 1 18 1z"
            fill="#e53935"
            stroke="#c62828"
            stroke-width="1"
          />
          <circle cx="18" cy="18" r="7" fill="white" opacity="0.95" />
          <circle cx="13" cy="13" r="2.5" fill="white" opacity="0.35" />
        </svg>
      </div>
      <div class="pin-shadow"></div>
    </div>

    <p v-if="loading" class="hint">Loading map...</p>
  </div>
</template>

<script>
import { importLibrary } from '@/services/googleMaps';

export default {
  name: 'AddressPickerMap',
  props: {
    initialCenter: {
      type: Object,
      default: () => ({ lat: 37.97555296173737, lng: 23.73490355885644 }),
    },
    boundaryCenter: {
      type: Object,
      default: null,
    },
    boundaryRadius: {
      type: Number,
      default: 50,
    },
  },
  data() {
    return {
      map: null,
      CircleClass: null,
      boundaryCircleInstance: null,
      geocoder: null,
      loading: true,
      isDragging: false,
      isSnapping: false,
      userHasDragged: false,
    };
  },
  async mounted() {
    try {
      const [{ Map, Circle }, { Geocoder }] = await Promise.all([
        importLibrary('maps', this.$i18n.locale),
        importLibrary('geocoding', this.$i18n.locale),
      ]);
      this.CircleClass = Circle;
      this.geocoder = new Geocoder();
      this.map = new Map(this.$refs.mapEl, {
        center: this.initialCenter,
        zoom: this.computeOptimalZoom(),
        fullscreenControl: false,
        streetViewControl: false,
        mapTypeControl: false,
      });
      this.drawBoundaryCircle();
      this.attachMapListeners();
    } catch (err) {
      console.error('Failed to load Google Maps:', err);
    } finally {
      this.loading = false;
    }
  },
  watch: {
    '$i18n.locale'(newLocale) {
      this.reinitializeForLocale(newLocale);
    },
  },
  methods: {
    computeOptimalZoom() {
      if (!this.boundaryCenter) return 14;
      // Target: circle diameter fills ~60% of the map height
      const mapHeight = this.$refs.mapEl?.clientHeight || 420;
      const targetPixels = (mapHeight * 0.6) / 2;
      const metersPerPixel = this.boundaryRadius / targetPixels;
      const metersPerPixelAtZoom0 =
        156543.03392 * Math.cos((this.boundaryCenter.lat * Math.PI) / 180);
      return Math.round(Math.log2(metersPerPixelAtZoom0 / metersPerPixel));
    },

    drawBoundaryCircle() {
      if (this.boundaryCircleInstance) {
        this.boundaryCircleInstance.setMap(null);
        this.boundaryCircleInstance = null;
      }
      if (!this.boundaryCenter || !this.CircleClass) return;
      this.boundaryCircleInstance = new this.CircleClass({
        map: this.map,
        center: this.boundaryCenter,
        radius: this.boundaryRadius,
        strokeColor: '#4285F4',
        strokeOpacity: 0.7,
        strokeWeight: 2,
        fillColor: '#4285F4',
        fillOpacity: 0.07,
        clickable: false,
      });
    },

    computeDistance(lat1, lng1, lat2, lng2) {
      const R = 6371000;
      const dLat = ((lat2 - lat1) * Math.PI) / 180;
      const dLng = ((lng2 - lng1) * Math.PI) / 180;
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((lat1 * Math.PI) / 180) *
          Math.cos((lat2 * Math.PI) / 180) *
          Math.sin(dLng / 2) ** 2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    },

    clampToCircle(lat, lng) {
      const bLat = this.boundaryCenter.lat;
      const bLng = this.boundaryCenter.lng;
      const angle = Math.atan2(lng - bLng, lat - bLat);
      const metersPerDegreeLat = 111320;
      const metersPerDegreeLng = 111320 * Math.cos((bLat * Math.PI) / 180);
      return {
        lat: bLat + (this.boundaryRadius / metersPerDegreeLat) * Math.cos(angle),
        lng: bLng + (this.boundaryRadius / metersPerDegreeLng) * Math.sin(angle),
      };
    },

    attachMapListeners() {
      this.map.addListener('dragstart', () => {
        this.isDragging = true;
        this.userHasDragged = true;
      });
      this.map.addListener('dragend', () => {
        this.isDragging = false;
      });
      this.map.addListener('idle', () => {
        if (this.isSnapping) {
          this.isSnapping = false;
          return;
        }

        if (this.boundaryCenter) {
          const center = this.map.getCenter();
          const dist = this.computeDistance(
            this.boundaryCenter.lat,
            this.boundaryCenter.lng,
            center.lat(),
            center.lng(),
          );
          if (dist > this.boundaryRadius) {
            const clamped = this.clampToCircle(center.lat(), center.lng());
            this.isSnapping = true;
            this.isDragging = false;
            this.$emit('out-of-bounds');
            this.map.panTo(clamped);
            return;
          }
        }

        this.geocodeCenter();
      });
    },

    reverseGeocodeCountry(lat, lng) {
      if (!this.geocoder) return Promise.resolve(null);
      return new Promise(resolve => {
        this.geocoder.geocode(
          { location: { lat, lng }, language: this.$i18n.locale },
          (results, status) => {
            if (status !== 'OK' || !results) {
              resolve(null);
              return;
            }
            for (const result of results) {
              const country = result.address_components?.find(c => c.types.includes('country'));
              if (country) {
                resolve(country.short_name);
                return;
              }
            }
            resolve(null);
          },
        );
      });
    },

    async reinitializeForLocale(locale) {
      let currentCenter = this.initialCenter;
      let currentZoom = this.computeOptimalZoom();
      if (this.map) {
        const c = this.map.getCenter();
        currentCenter = { lat: c.lat(), lng: c.lng() };
        currentZoom = this.map.getZoom();
      }

      if (this.boundaryCircleInstance) this.boundaryCircleInstance.setMap(null);
      this.boundaryCircleInstance = null;
      this.map = null;
      this.CircleClass = null;
      this.geocoder = null;
      this.isDragging = false;
      this.isSnapping = false;
      this.loading = true;

      try {
        const [{ Map, Circle }, { Geocoder }] = await Promise.all([
          importLibrary('maps', locale),
          importLibrary('geocoding', locale),
        ]);
        if (!this.$refs.mapEl) return;
        this.CircleClass = Circle;
        this.geocoder = new Geocoder();
        this.map = new Map(this.$refs.mapEl, {
          center: currentCenter,
          zoom: currentZoom,
          fullscreenControl: false,
          streetViewControl: false,
          mapTypeControl: false,
        });
        this.drawBoundaryCircle();
        this.attachMapListeners();
      } catch (err) {
        console.error('Failed to reload Google Maps:', err);
      } finally {
        this.loading = false;
      }
    },

    geocodeCenter() {
      if (!this.map) return;
      const center = this.map.getCenter();
      this.$emit('address-picked', {
        lat: center.lat(),
        lng: center.lng(),
        userDragged: this.userHasDragged,
      });
    },

    // Re-target an already-instantiated map at the current props. Called by the
    // parent on every modal reopen so we don't bill another Dynamic Maps Load.
    applyConfig() {
      if (!this.map) return;
      this.userHasDragged = false;
      this.isDragging = false;
      this.isSnapping = false;
      this.map.setCenter(this.initialCenter);
      this.map.setZoom(this.computeOptimalZoom());
      this.drawBoundaryCircle();
    },
  },
};
</script>

<style scoped>
.map-wrapper {
  position: relative;
  width: 100%;
  flex-shrink: 0;
}

.map-canvas {
  width: 100%;
  height: 420px;
  height: min(420px, 50dvh);
  border-radius: 6px;
  touch-action: none;
}

@media only screen and (max-width: 600px) {
  .map-canvas {
    height: 260px;
    height: clamp(170px, 40dvh, 260px);
  }
}

.pin-container {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  pointer-events: none;
  z-index: 1;
}

.pin-body {
  position: absolute;
  bottom: 0;
  left: -18px;
  filter: drop-shadow(0 3px 5px rgba(0, 0, 0, 0.32));
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.pin-container.dragging .pin-body {
  transform: translateY(-14px);
  filter: drop-shadow(0 10px 10px rgba(0, 0, 0, 0.22));
  transition: transform 0.18s ease-out, filter 0.18s ease-out;
}

.pin-shadow {
  position: absolute;
  top: -3px;
  left: -8px;
  width: 16px;
  height: 6px;
  background: rgba(0, 0, 0, 0.28);
  border-radius: 50%;
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.35s ease;
}

.pin-container.dragging .pin-shadow {
  transform: scale(0.45);
  opacity: 0.25;
  transition: transform 0.18s ease-out, opacity 0.18s ease-out;
}

.hint {
  font-size: 12px;
  color: #777;
}
</style>
